<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import HotelBookingsAPI from 'dashboard/api/hotelBookings';
import MessageApi from 'dashboard/api/inbox/message';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  booking: { type: Object, required: true },
  contact: { type: Object, required: true },
  conversationId: { type: [Number, String], required: true },
  quoteData: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['confirmed', 'cancel']);

const guestName = ref(props.booking.guest_name || props.contact?.name || '');
const guestEmail = ref(props.booking.guest_email || props.contact?.email || '');
const guestPhone = ref(
  props.booking.guest_phone || props.contact?.phone_number || ''
);
const guestCpf = ref(props.booking.guest_cpf || '');
const billingType = ref('PIX');
const isSubmitting = ref(false);
const isSendingLink = ref(false);
const paymentResult = ref(null);

const config = computed(() => props.quoteData || {});

const paymentOptions = computed(() => {
  const opts = [];
  if (config.value.pix_enabled !== false) {
    opts.push({ value: 'PIX', label: 'PIX' });
  }
  opts.push({ value: 'BOLETO', label: 'Boleto Bancario' });
  if (config.value.credit_card_enabled) {
    opts.push({ value: 'CREDIT_CARD', label: 'Cartao de Credito (link)' });
  }
  opts.push({ value: 'on_arrival', label: 'Pagamento no Hotel' });
  return opts;
});

const formatCurrency = value =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = date => {
  if (!date) return '-';
  return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR');
};

const isFormValid = computed(
  () => guestName.value.trim() && guestCpf.value.trim()
);

const handleConfirm = async () => {
  if (!isFormValid.value) return;
  isSubmitting.value = true;
  try {
    const { data } = await HotelBookingsAPI.generatePayment(props.booking.id, {
      guest_name: guestName.value.trim(),
      guest_email: guestEmail.value.trim(),
      guest_phone: guestPhone.value.trim(),
      guest_cpf: guestCpf.value.trim(),
      payment_method: billingType.value.toLowerCase(),
      billing_type: billingType.value,
    });
    paymentResult.value = data.payment_result || data;
    useAlert('Reserva confirmada e pagamento gerado!');
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao confirmar reserva.';
    useAlert(msg);
  } finally {
    isSubmitting.value = false;
  }
};

const paymentUrl = computed(() => {
  if (!paymentResult.value) return null;
  return (
    paymentResult.value.invoice_url || paymentResult.value.bank_slip_url || null
  );
});

const sendPaymentLink = async () => {
  if (!paymentUrl.value) return;
  isSendingLink.value = true;
  try {
    const room = props.booking.motor_reserva_room;
    const lines = [
      `✅ *Reserva Confirmada #${props.booking.id}*`,
      '',
      `🏨 *Quarto:* ${room?.name || props.booking.room_name || '-'}`,
      `📅 *Check-in:* ${formatDate(props.booking.check_in)}`,
      `📅 *Check-out:* ${formatDate(props.booking.check_out)}`,
      `💰 *Total:* ${formatCurrency(props.booking.total_price)}`,
      '',
      `💳 *Forma de Pagamento:* ${billingType.value}`,
      '',
      `🔗 *Link de Pagamento:*`,
      paymentUrl.value,
    ];

    if (billingType.value === 'PIX' && paymentResult.value?.pix_payload) {
      lines.push('', '📱 *Codigo PIX (copia e cola):*');
      lines.push(paymentResult.value.pix_payload);
    }

    if (config.value.property_name) {
      lines.push('', `🏨 ${config.value.property_name}`);
    }

    await MessageApi.create({
      conversationId: props.conversationId,
      message: lines.join('\n'),
      private: false,
    });
    useAlert('Link de pagamento enviado na conversa!');
  } catch {
    useAlert('Erro ao enviar link.');
  } finally {
    isSendingLink.value = false;
  }
};

const ic =
  'w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none';
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <!-- Header -->
    <div
      class="flex items-center gap-2 p-3 bg-n-green-2 rounded-lg border border-n-green-7"
    >
      <span class="text-xl">✅</span>
      <div>
        <div class="text-sm font-semibold text-n-green-11">
          Confirmar Reserva #{{ booking.id }}
        </div>
        <div class="text-xs text-n-green-10">
          {{ formatCurrency(booking.total_price) }} -
          {{ formatDate(booking.check_in) }} a
          {{ formatDate(booking.check_out) }}
        </div>
      </div>
    </div>

    <!-- Guest data -->
    <div class="flex flex-col gap-3">
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >Nome do Hospede</label
        >
        <input v-model="guestName" type="text" :class="ic" />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >CPF</label
          >
          <input
            v-model="guestCpf"
            type="text"
            :class="ic"
            placeholder="000.000.000-00"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Telefone</label
          >
          <input v-model="guestPhone" type="text" :class="ic" />
        </div>
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >E-mail</label
        >
        <input v-model="guestEmail" type="email" :class="ic" />
      </div>
    </div>

    <!-- Payment method -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-2"
        >Forma de Pagamento</label
      >
      <div class="flex flex-col gap-2">
        <label
          v-for="opt in paymentOptions"
          :key="opt.value"
          class="flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors"
          :class="
            billingType === opt.value
              ? 'border-n-brand-7 bg-n-brand-2'
              : 'border-n-weak bg-n-surface-1'
          "
        >
          <input
            v-model="billingType"
            type="radio"
            :value="opt.value"
            class="text-n-brand-9"
          />
          <span
            class="text-sm"
            :class="
              billingType === opt.value
                ? 'text-n-brand-11 font-medium'
                : 'text-n-slate-11'
            "
          >
            {{ opt.label }}
          </span>
        </label>
      </div>
    </div>

    <!-- Payment result -->
    <div
      v-if="paymentResult"
      class="bg-n-green-2 border border-n-green-7 rounded-lg p-4"
    >
      <div class="text-sm font-semibold text-n-green-11 mb-2">
        Pagamento gerado com sucesso!
      </div>
      <div v-if="paymentUrl" class="flex flex-col gap-2">
        <a
          :href="paymentUrl"
          target="_blank"
          rel="noopener"
          class="text-xs text-n-brand-11 underline break-all"
        >
          {{ paymentUrl }}
        </a>
        <Button
          label="Enviar Link ao Cliente"
          sm
          :disabled="isSendingLink"
          :is-loading="isSendingLink"
          @click="sendPaymentLink"
        />
      </div>
      <div
        v-if="paymentResult.pix_payload"
        class="mt-3 p-2 bg-n-surface-1 rounded text-xs text-n-slate-11 break-all"
      >
        <span class="font-medium">PIX Copia e Cola:</span>
        {{ paymentResult.pix_payload }}
      </div>
    </div>

    <!-- Actions -->
    <div v-if="!paymentResult" class="flex items-center gap-2 pt-2">
      <Button label="Cancelar" sm slate @click="emit('cancel')" />
      <div class="flex-1" />
      <Button
        label="Confirmar e Gerar Pagamento"
        sm
        :disabled="!isFormValid || isSubmitting"
        :is-loading="isSubmitting"
        @click="handleConfirm"
      />
    </div>
    <div v-else class="flex justify-end pt-2">
      <Button label="Fechar" sm slate @click="emit('confirmed')" />
    </div>
  </div>
</template>
